// @flow
/* -----------------Globals--------------- */
import React, { useEffect, useState } from 'react';

/* -----------------Third parties--------------- */
import dynamic from 'next/dynamic';
import Router from 'next/router';

import {
  Container,
  Box,
} from '@material-ui/core';
import {
  withStyles,
} from '@material-ui/styles/';

/* -----------------Types--------------- */
import {
  type TBreadcrumbs,
} from '@components/common/appbarutils';
import {
  authenticated,
  key,
} from '../../common/authutils';

/* -----------------Child components--------------- */
import ErrorComponent from '@components/common/ErrorComponent';
import AppContentHeaderCommon from '@components/common/AppContentHeaderCommon';
import TermCard from '@components/common/TermCard';
import TermCardWrapper from '@components/common/TermCardWrapper';
import Loader from '@components/loading/StudySetDetailsSkeleton';
import CardGuestUserRegister from '@components/common/CardGuestUserRegister';
import IconSkeleton from '../loading/IconSkeleton';

/* -----------------Lazy components--------------- */
const DialogAuth = dynamic(() => import('../auth/DialogAuth'), { ssr: false });

const styles = () => ({
  container: {
    userSelect: 'none',
  },
});
type Classes = {
  container: string
}
/* -----------------Props--------------- */
type Props = {
  intentId?: string,
  cachedStudySetDetails: *,
  cachedBreadcrumbs: TBreadcrumbs[],
  classes: Classes,
  showContentMenuSkeleton?: boolean
}

/* -----------------Component--------------- */
const StudySetUnAuth = (p: Props) => {
  const {
    intentId,
    cachedStudySetDetails,
    cachedBreadcrumbs,
    classes,
    showContentMenuSkeleton,
  } = p;

  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  useEffect(() => {
    if (intentId) {
      setAuthDialogOpen(true);
    }
  }, [intentId]);

  const handleClose = () => {
    setAuthDialogOpen(false);
  };

  if (!cachedStudySetDetails) {
    return <Loader />;
  }
  if (intentId) {
    return (
      <>
        {<ErrorComponent statusCode={401} />}
        {authDialogOpen ? (
          <DialogAuth
            handleClose={handleClose}
            initialView="register"
            open={authDialogOpen}
          />
        ) : null}
      </>
    );
  }

  const {
    studySet: cachedStudyset,
    studySet: {
      id: cachedId,
      // accessType,
      terms: {
        edges,
      },
      itemsCount: {
        numFacts,
      },
    },
    metadata: cachedMetadata = {
      isFavorited: false,
      distribution: {
        none: numFacts,
        high: 0,
        low: 0,
        medium: 0,
      },
      permissions: [],
    },
  } = cachedStudySetDetails;

  const renderContentMenu = () => (showContentMenuSkeleton ? <IconSkeleton /> : null);
  const isAuthenticated = authenticated(key.loggedIn);

  return (
    <div
      className={classes.container}
    >
      <AppContentHeaderCommon
        content={cachedStudyset}
        metadata={cachedMetadata}
        variant="content"
        shouldShowFab
        renderContentMenu={renderContentMenu}
        breadcrumbs={cachedBreadcrumbs}
        hasAccess={showContentMenuSkeleton ? undefined : false}
        contentInputs={[{
          contentId: cachedId,
          contentType: 'STUDY_SET',
        }]}
      />
      <Container maxWidth="lg">
        <Box py={6}>
          {edges.map(({
            node: {
              term,
              metadata,
            },
          }, index) => {
            const showGuestUserCta = index === 5 && !isAuthenticated;
            const showBlur = index > 5 && !isAuthenticated;
            return (
              <>
                {
                showGuestUserCta ? (
                  <Box
                    position="sticky"
                    top={72}
                    display="flex"
                    zIndex={1500}
                    justifyContent="center"
                    width="100%"
                    mb={1}
                  >
                    <CardGuestUserRegister
                      redirectUrl={Router.asPath}
                      cta="Want to study these flashcards and millions more?"
                    />
                  </Box>

                ) : null}

                <TermCardWrapper key={term.id}>
                  <TermCard
                    term={term}
                    metadata={metadata}
                    loading={false}
                    studySetId={cachedId}
                    index={index}
                    showBlur={showBlur}
                  />
                </TermCardWrapper>
              </>
            );
          })}
        </Box>
      </Container>
    </div>
  );
};

export default withStyles(styles)(StudySetUnAuth);
